import api from './api';
import {
  getAccessToken,
  clearAuthTokens,
  getPersistedClientPortalFlags,
  mergeClientPortalFlags,
  persistClientPortalFlags,
} from './auth';
import {
  getEmailFromToken,
  getRoleFromToken,
  getAccountTypeFromToken,
} from './tokenClaims';
import { clearSession, getSession, setSession } from './sessionStore';
import { isHospitalRole, normalizeHospitalRole } from './userDashboardPermissions';

function parseAdminDetailsBody(data) {
  if (!data || typeof data !== 'object') return { name: '', role: '' };
  const inner = data.user ?? data.data ?? data.admin ?? data.record;
  const src = inner && typeof inner === 'object' ? { ...data, ...inner } : data;
  const role = src.role ?? '';
  return { role: String(role).trim() };
}

async function fetchClientLoginExtras() {
  try {
    const res = await api.get('/client/login-redirect');
    if (!res.data?.success) return { ok: false };
    return {
      ok: true,
      allowUserAppOnWeb: res.data.allowUserAppOnWeb,
      allowAccessWithoutPayment: res.data.allowAccessWithoutPayment,
      canAccessRobots: res.data.canAccessRobots,
      redirectRoute: res.data.redirectRoute,
    };
  } catch {
    return { ok: false };
  }
}

function applyClientPortalFlags(target, flags) {
  if (!flags) return;
  target.allowUserAppOnWeb = Boolean(flags.allowUserAppOnWeb);
  target.allowAccessWithoutPayment = Boolean(flags.allowAccessWithoutPayment);
  target.canAccessRobots = Boolean(flags.canAccessRobots);
}

function isCustomerSession(accountType, role) {
  if (accountType === 'customer') return true;
  if (accountType === 'admin' || accountType === 'robotController') return false;
  return isHospitalRole(role);
}

/**
 * Validates the current token against the backend and hydrates in-memory session.
 * @param {object} [loginPayload] — optional fresh login response fields
 */
export async function bootstrapSession(loginPayload = null) {
  const token = getAccessToken();
  if (!token) {
    clearSession();
    return null;
  }

  if (loginPayload) {
    const portalFlags = {
      allowUserAppOnWeb: Boolean(loginPayload.allowUserAppOnWeb),
      allowAccessWithoutPayment: Boolean(loginPayload.allowAccessWithoutPayment),
      canAccessRobots: Boolean(loginPayload.canAccessRobots),
    };
    const accountType = String(loginPayload.accountType || '').trim();
    let role = String(loginPayload.role || '').trim();
    if (accountType === 'customer' || isHospitalRole(role)) {
      role = normalizeHospitalRole(role);
    }
    setSession({
      email: loginPayload.email || '',
      role,
      accountType: accountType || (isHospitalRole(role) ? 'customer' : ''),
      ...portalFlags,
      subscriptionAdmin: loginPayload.subscriptionAdmin || null,
    });
    return getSession();
  }

  const emailFromToken = getEmailFromToken(token);
  const roleFromToken = getRoleFromToken(token);
  const accountTypeFromToken = getAccountTypeFromToken(token);

  if (!emailFromToken) {
    clearSession();
    clearAuthTokens();
    return null;
  }

  let role = roleFromToken;
  let accountType = accountTypeFromToken;
  try {
    const res = await api.get(`getemail-admin-details/${encodeURIComponent(emailFromToken)}`);
    const parsed = parseAdminDetailsBody(res.data);
    if (parsed.role) role = parsed.role;
  } catch {
    if (!role) {
      clearSession();
      clearAuthTokens();
      return null;
    }
  }

  const customer = isCustomerSession(accountType, role);
  if (customer) {
    role = normalizeHospitalRole(role);
    accountType = accountType || 'customer';
  }

  const base = {
    email: emailFromToken,
    role,
    accountType,
    allowUserAppOnWeb: false,
    allowAccessWithoutPayment: false,
    canAccessRobots: false,
    subscriptionAdmin: null,
  };

  if (customer) {
    const persisted = getPersistedClientPortalFlags();
    const extras = await fetchClientLoginExtras();
    if (extras.ok) {
      const merged = mergeClientPortalFlags(extras, persisted);
      applyClientPortalFlags(base, merged);
      persistClientPortalFlags(merged);
    } else {
      applyClientPortalFlags(base, persisted);
    }
  } else {
    try {
      const { subscriptionAdminApi } = await import('../subscriptionAdmin/api');
      const authInfo = await subscriptionAdminApi.authInfo();
      if (authInfo) {
        base.subscriptionAdmin = authInfo;
      }
    } catch {
      /* subscription admin module optional */
    }
  }

  setSession(base);
  return getSession();
}
